import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "cashier" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ email: "", name: "", password: "", role: "cashier" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <Link to="/settings" className="label-eyebrow hover:text-ink transition-colors">← Settings</Link>
        <h1 className="font-heading text-4xl font-bold mt-1">Users</h1>
        <p className="text-sm text-muted mt-1">Manage admin and cashier accounts.</p>
      </div>

      <Card className="p-6 border border-line">
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <Label className="label-eyebrow">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="label-eyebrow">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="label-eyebrow">Password</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="user-password-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="label-eyebrow">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cashier">Cashier</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={submitting} data-testid="add-user-btn" className="bg-ink hover:bg-ink-soft text-white">
            <UserPlus size={16} className="mr-1.5" /> Add user
          </Button>
        </form>
      </Card>

      <Card className="overflow-hidden border border-line">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-paper-alt border-b border-line">
                <th className="text-left px-4 py-3 label-eyebrow">Name</th>
                <th className="text-left px-4 py-3 label-eyebrow">Email</th>
                <th className="text-left px-4 py-3 label-eyebrow">Role</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody data-testid="users-rows">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-line hover:bg-paper/60">
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-muted text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-md ${u.role === "admin" ? "bg-ink text-white" : "bg-paper-alt text-ink"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.id !== me?.id && (
                      <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="text-muted hover:text-cashout transition-colors">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
